import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Loader } from "../Components/Loader";
import { Api } from "../Utils/Api";
import { ErrorModal } from "../Components/Erro";
import { DiagnosticRelatedInformation } from "typescript";
import { api_login_unico_url } from "../Utils/Urls";
import { Notificacao, NotificacaoComponent } from "../Components/Notificacao";
import { RegistroProtocoloEletronico } from "../Utils/RegistroDemaisSistema";

const UsuarioContext = createContext({} as ContextData);

interface ContextData {
  usuario: IUsuario;
  logar: (
    usuario: string,
    senha: string,
    tipo?: "id" | "cpf" | "usuario" | "cnpj",
    manterAutenticado?: boolean
  ) => Promise<void>;
  sair: () => void;
  token: string;
  contasMesmoUsuario: UsuarioIncompleto[];
  manterAutenticado: string | null;
  setManterAutenticado: React.Dispatch<React.SetStateAction<string | null>>;
  carregarUsuariosPorCpfOuCnpj: (
    cpf_cnpj: string,
    tipo: "cpf" | "cnpj"
  ) => Promise<UsuarioIncompleto[]>

  trocarConta: (id_usuario: number) => Promise<boolean>;
  registrarEmEntidade: (
    entidade_id: number,
    aplicarSessao?: boolean
  ) => Promise<boolean>;
  carregarMeusSistemas: () => Promise<void>;
}

function useUsuario() {
  const context = useContext(UsuarioContext);
  if (!context) {
    throw new Error("useUsuario deve ser usado dentro de um UsuarioProvider");
  }
  return context;
}

interface IProviderProps {
  children: ReactNode;
}

export interface UsuarioIncompleto {
  id: number;
  entidade_id: number;
  usuario: string;
  senha: string;
  cpf: string;
  cnpj: string;
  administrador: boolean;
  email: string;
  inativo: boolean;
  nome: string;
  foto: string;
  telefone: string;
  descricao: string;
  servidor: boolean;
  endereco: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  sexo: string;
  data_nascimento: string;
  data_cadastro: string;
  ultimo_login: string;
  perfil: string;
  observacoes: string;
  zona_rural: boolean;
  localidade_rural: string;
  complemento: string;
}
export interface IUsuario {
  usuario: {
    id: number;
    entidade_id: number;
    usuario: string;
    senha: string;
    cpf: string;
    cnpj: string;
    administrador: boolean;
    email: string;
    inativo?: Date;
    nome: string;
    foto: string;
    telefone: string;
    descricao: string;
    servidor: boolean;
    endereco: string;
    bairro: string;
    cidade: string;
    uf: string;
    cep: string;
    sexo: string;
    data_nascimento: Date;
    data_cadastro: Date;
    ultimo_login: Date;
    perfil: string;
    observacoes: string;
    zona_rural: boolean;
    localidade_rural: string;
    complemento: string;
  };

  entidade: {
    id: number;
    titulo: string;
    banco: string;
    logo: string;
    apresentacao: string;
    portal: string;
    site: string;
    site2: string;
    pasta: string;
  };
  sistemas: ISistema[];
  _searchIndex?: string;
}

function UsuarioProvider({ children }: IProviderProps) {





  const [usuario, setUsuario] = useState({} as IUsuario);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState({ titulo: "", descricao: "" });
  const [token, setToken] = useState("");
  const [contasMesmoUsuario, setContasMesmoUsuario] = useState(
    [] as UsuarioIncompleto[]
  );
  const [notificacao, setNotificacao] = useState({
    mensagem: "",
    tipo: "AVISO",
  } as Notificacao);
  const chave_localStorage = "@login-unico";

  const searchParams = new URLSearchParams(window.location.search);
  const url = searchParams.get("url");

  const [manterAutenticado, setManterAutenticado] = useState<string | null>(
    () => {
      // Pega o valor inicial do localStorage
      const savedValue = localStorage.getItem("@manter-autenticado");
      return savedValue; // Converte para booleano
    }
  );

  useEffect(() => {
    //atualizando usuario nos sistemas dependentes
    if (usuario?.usuario?.id && token)
      RegistroProtocoloEletronico(usuario.usuario.id, token);
  }, [usuario, token])

  //VERIFICANDO POR MATRICULA SE O USUARIO QUE ESTA LOGADO É UM SERVIDOR DA PREFEITURA
  // Quem consulta a folha e decide é o backend (POST /marcar-servidor): desde as
  // correções de permissões o `servidor` é campo só-admin e o antigo PUT / com
  // { servidor: 1 } era descartado em silêncio.
  useEffect(() => {
    const verificarMatricula = async () => {
      if (!token || !usuario?.usuario?.id || !usuario?.usuario?.cpf) return;
      if (usuario.usuario.servidor) return; // já é servidor

      try {
        // token passado explicitamente: este effect roda antes do que coloca
        // o token no Api.defaults, então logo após o login o header ainda
        // estaria vazio (ou com o token da conta anterior)
        const { data } = await Api.post("/marcar-servidor", null, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // só reloga se realmente marcou: o token novo leva servidor = 1, e é
        // dele que o /meus-sistemas tira a liberação do Portal do Servidor
        if (data?.status && data?.data?.alterado) {
          await logar(
            usuario.usuario.id.toString(),
            usuario.usuario.senha,
            "id"
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    verificarMatricula();
  }, [usuario?.usuario?.id, token]);


  async function logar(
    usuario: string,
    senha: string,
    tipo?: "id" | "cpf" | "usuario" | "cnpj",
    manterAutenticado?: boolean
  ) {
    if (!tipo) tipo = "usuario";
    try {
      setLoading(true);
      const { data } = await Api.post(`/login-completo`, {
        usuario: usuario,
        senha: senha,
        tipo,
      });


      if (!data.status) {
        setContasMesmoUsuario(data.data);

        if (data.data && data.data.length > 1) {
          setNotificacao({ mensagem: data.mensagem, tipo: "AVISO" });
        } else {
          setErro({
            titulo: "Erro ao Efeturar Login",
            descricao: data.mensagem,
          });
        }
        //setNotif
      } else {
        if (
          data.data.login.usuario.foto &&
          data.data.login.usuario.foto.length > 0
        ) {
          data.data.login.usuario.foto =
            api_login_unico_url +
            "/foto-de-perfil/" +
            data.data.login.usuario.foto;
        } else {
          data.data.login.usuario.foto = null;
        }

        setUsuario(data.data.login);
        setToken(data.data.token);
        if (manterAutenticado) {
          setManterAutenticado(
            `{"id":"${data.data.login.usuario.id}","senha":"${data.data.login.usuario.senha}"}`
          );
        }
        if (url) {
          window.location.href = url;
        }

      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  async function trocarConta(
    id_usuario: number,
    manterAutenticado?: boolean
  ): Promise<boolean> {
    try {
      setLoading(true);
      const { data } = await Api.post(`/login-automatico`, {
        id_usuario: id_usuario,
      });

      if (!data.status) {
        setErro({
          titulo: "Erro ao acessar outra conta.",
          descricao: data.mensagem,
        });

        return false;

      } else {
        if (
          data.data.login.usuario.foto &&
          data.data.login.usuario.foto.length > 0
        ) {
          data.data.login.usuario.foto =
            api_login_unico_url +
            "/foto-de-perfil/" +
            data.data.login.usuario.foto;
        } else {
          data.data.login.usuario.foto = null;
        }

        setUsuario(data.data.login);
        setToken(data.data.token);

        localStorage.setItem(chave_localStorage, data.data.token);
        Api.defaults.headers.common["Authorization"] = `Bearer ${data.data.token}`;

        // Se a sessão estava "mantida", aponta o manter-autenticado para a NOVA conta.
        // (senão, ao recarregar, o app re-logaria a conta ANTERIOR)
        const sessaoMantida =
          manterAutenticado || !!localStorage.getItem("@manter-autenticado");
        if (sessaoMantida) {
          const novoValor = `{"id":"${data.data.login.usuario.id}","senha":"${data.data.login.usuario.senha}"}`;
          setManterAutenticado(novoValor);
          localStorage.setItem("@manter-autenticado", novoValor); // direto, evita corrida com o reload
        }

        return true;

        //pra que isso serve?
        /* if (url) {
          window.location.href = url;
        } */

      }
    } catch (e) {
      console.log(e);
      return false;
    } finally {
      setLoading(false);
    }
  }


  /**
   * Registra o usuário autenticado em uma NOVA entidade.
   *
   * @param aplicarSessao quando true (padrão), já ENTRA na conta criada — é o
   *   comportamento de 1 clique do "Trocar de conta".
   *   Passe FALSE para registrar em lote: chamado em laço com true, cada volta
   *   trocaria a sessão e você terminaria logado na última entidade da lista.
   *   Com false, o backend cria a conta normalmente e o token atual é mantido.
   */
  async function registrarEmEntidade(
    entidade_id: number,
    aplicarSessao: boolean = true
  ): Promise<boolean> {
    try {
      setLoading(true);
      const { data } = await Api.post(`/registrar-em-entidade`, { entidade_id });

      if (!data.status) {
        // Em lote o erro de uma entidade não pode abrir diálogo por cima da
        // tela: quem chamou decide o que mostrar no fim.
        if (aplicarSessao) {
          setErro({
            titulo: "Erro ao registrar na entidade.",
            descricao: data.mensagem,
          });
        }
        return false;
      }

      if (!aplicarSessao) return true;

      // aplica o login retornado (igual à troca de conta)
      if (data.data.login.usuario.foto && data.data.login.usuario.foto.length > 0) {
        data.data.login.usuario.foto =
          api_login_unico_url + "/foto-de-perfil/" + data.data.login.usuario.foto;
      } else {
        data.data.login.usuario.foto = null;
      }

      setUsuario(data.data.login);
      setToken(data.data.token);
      localStorage.setItem(chave_localStorage, data.data.token);
      Api.defaults.headers.common["Authorization"] = `Bearer ${data.data.token}`;

      // se a sessão estava "mantida", aponta para a NOVA conta
      if (localStorage.getItem("@manter-autenticado")) {
        const novoValor = `{"id":"${data.data.login.usuario.id}","senha":"${data.data.login.usuario.senha}"}`;
        setManterAutenticado(novoValor);
        localStorage.setItem("@manter-autenticado", novoValor);
      }

      return true;
    } catch (e) {
      console.log(e);
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function carregarUsuariosPorCpfOuCnpj(
    cpf_cnpj: string,
    tipo: "cpf" | "cnpj"
  ): Promise<UsuarioIncompleto[]> {

    try {
      setLoading(true);
      const { data } = await Api.get(`/por-cpf-cnpj/${cpf_cnpj}`);
      console.log(data.data)
      return data.data;




    } catch (e) {
      console.log(e);
      const lista: UsuarioIncompleto[] = [];
      return lista;
    } finally {
      setLoading(false);
    }
  }

  // busca a lista de sistemas do usuário pela rota dedicada (fora do token)
  async function carregarMeusSistemas() {
    try {
      const { data } = await Api.get(`/meus-sistemas`);
      if (data.status) {
        setUsuario((prev) => ({ ...prev, sistemas: data.data }));
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function checarToken(ignorarManterAutenticado = false) {
    if (manterAutenticado && !ignorarManterAutenticado) {
      const objetoAutenticar = JSON.parse(manterAutenticado);
      logar(objetoAutenticar.id, objetoAutenticar.senha, "id", true);
    } else {
      try {
        setLoading(true);
        // /checar-token reconstrói usuário + entidade do banco
        const { data } = await Api.get(`/checar-token`);

        if (data.usuario.usuario.foto && data.usuario.usuario.foto.length > 0) {
          data.usuario.usuario.foto =
            api_login_unico_url +
            "/foto-de-perfil/" +
            data.usuario.usuario.foto;
        } else {
          data.usuario.usuario.foto = null;
        }

        setUsuario(data.usuario);

        // os sistemas não vêm no token nem no /checar-token: busca na rota dedicada
        await carregarMeusSistemas();
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    }
  }

  function sair() {
    setManterAutenticado(null);
    localStorage.removeItem(chave_localStorage);
    setUsuario({} as IUsuario);
    setToken("");
  }

  useEffect(() => {
    if (token) {
      localStorage.setItem(chave_localStorage, token);

      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      if (usuario?.usuario?.id) {
        //IMPORTAÇÃO AUTOMATICA DADOS USUÁRIO
        RegistroProtocoloEletronico(usuario.usuario.id, token);
      }
    }
  }, [usuario, token]);

  useEffect(() => {
    const iniciarAutenticacao = async () => {
      const tokenDaUrl = searchParams.get("token");
      const tokenLocalStorage = localStorage.getItem(chave_localStorage);

      const tokenFinal = tokenDaUrl || tokenLocalStorage;

      if (tokenFinal) {
        localStorage.setItem(chave_localStorage, tokenFinal);
        Api.defaults.headers.common["Authorization"] = `Bearer ${tokenFinal}`;
        setToken(tokenFinal);
      }

      await checarToken(!!tokenDaUrl);

      if (tokenDaUrl) {
        const novaUrl = new URL(window.location.href);
        novaUrl.searchParams.delete("token");

        window.history.replaceState({}, document.title, novaUrl.toString());
      }
    };

    iniciarAutenticacao();
  }, []);

  useEffect(() => {
    if (manterAutenticado) {
      localStorage.setItem("@manter-autenticado", manterAutenticado);
    } else {
      localStorage.removeItem("@manter-autenticado");
    }
  }, [manterAutenticado]);

  useEffect(() => {
    if (usuario.usuario) {
      if (window.innerWidth <= 768) {
        setManterAutenticado(
          `{"id":"${usuario.usuario.id}","senha":"${usuario.usuario.senha}"}`
        );
      }
    }
  }, [usuario]);

  return (
    <UsuarioContext.Provider
      value={{
        usuario,
        logar,
        sair,
        token,
        contasMesmoUsuario,
        manterAutenticado,
        setManterAutenticado,
        carregarUsuariosPorCpfOuCnpj,
        trocarConta,
        registrarEmEntidade,
        carregarMeusSistemas
      }}
    >

      <Loader loading={loading} />
      <ErrorModal
        description={erro.descricao}
        title={erro.titulo}
        onClose={() => setErro({ descricao: "", titulo: "" })}
        open={erro.titulo.length > 0 && erro.descricao.length > 0}
      />
      <NotificacaoComponent
        notificacao={notificacao}
        setNotificacao={setNotificacao}
      />

      {children}
      {/* {JSON.stringify(token)} */}
    </UsuarioContext.Provider>
  );
}

export { UsuarioProvider, useUsuario };

export interface ISistema {
  id: number;
  titulo: string;
  url: string;
  usuario_id: number;
  sistema_id: number;
  logo: string;
  descricao: string;
}
